import {
  type IDropdownItem,
  type IFilterDropdownGroups,
  type IOrderedGroups,
  ItemsWithCount,
} from 'shared/ui'
import { DayjsFormats } from 'shared/lib'
import { EmptyCell, type IColumn } from 'shared/ui/TableNew'
import { uiStore } from 'shared/store/uiStore'
import type { IImportField } from 'entities/Contacts/api/types'
import { type RowContact } from 'entities/Contacts/model/Table/RowContact'
import { getIcon } from 'widgets/constants'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import { getCellForTags, getCellNumber } from 'widgets/ContactEnrollment/ui/EnrollmentContactsTable'
import { getCellForName } from 'widgets/ContactEnrollment/ui/EnrollmentContactsTable/helpers/helpers'
import { type FilterItemConfig } from 'widgets/FilterEditor/model/FilterItemConfig.ts'
import { tableColumnsKeys } from '../constants/tableColumnsConsts'
import { KeywordItem } from '../ui/KeywordItem/KeywordItem'

// No all clearedField has names as in the current design. Change it manually
const nameExceptions: Record<string, string> = {
  name: 'Name',
  number: 'Phone',
  email: 'Email',
  created_at: 'Created At',
}

const getInitGroups = (groupsNames: Record<string, string>): IFilterDropdownGroups => {
  const groups: IFilterDropdownGroups = {}
  const keys = Object.keys(groupsNames)

  keys.forEach((group) => {
    groups[group] = {
      label: groupsNames[group],
      items: [],
    }
  })

  return groups
}

const getOrderedGroups = (groupNames: Record<string, string>, groups: IFilterDropdownGroups) => {
  const orderedGroups: IOrderedGroups[] = []
  const keys = Object.keys(groupNames)

  keys.forEach((group) => {
    orderedGroups.push({
      label: groupNames[group],
      items: groups[group].items,
    })
  })

  return orderedGroups
}

const getCorrectDropdownItemLabel = ({
  name,
  key,
  category,
}: {
  name: string
  key: string
  category: string
}) => {
  const label = nameExceptions[key] || name
  if (category === 'recurring') {
    return `Recurring ${label.toLocaleLowerCase()}`
  }
  return label
}

const importContactGroupsName: Record<string, string> = {
  default_fields: 'Default fields',
  custom: 'Custom fields',
}

export const getImportContactDropdownGroups = (fields: IImportField[], mappedKeys: string[]) => {
  const groups: IFilterDropdownGroups = getInitGroups(importContactGroupsName)

  fields.forEach((filter) => {
    const dropdownItem: IDropdownItem = {
      id: filter.key,
      label: getCorrectDropdownItemLabel({
        category: filter.category_new,
        name: filter.name,
        key: filter.key,
      }),
      disabled: mappedKeys.includes(filter.key),
      value: filter.name,
      iconL: getIcon(filter),
      parentTooltipProps: {
        label: mappedKeys.includes(filter.key) ? 'Field is already selected' : filter.name,
        placement: 'left',
        fullWidth: true,
      },
    }

    const groupName = filter.key.includes('custom.') ? 'custom' : 'default_fields'

    groups[groupName].items.push(dropdownItem)
  })

  return getOrderedGroups(importContactGroupsName, groups)
}

const filterTransformer = ({ filters, customFilters }: IFiltersResponseList) => {
  const preparedFilters: { [key: string]: string } = {}

  filters.forEach((filter) => {
    preparedFilters[filter.key] = filter.name
  })

  customFilters.forEach((filter) => {
    const filterKey = filter.key.split('.').pop()

    if (filterKey) {
      preparedFilters[filterKey] = filter.name
    }
  })

  return preparedFilters
}

export const getCustomRowCellValue = (row: RowContact, column: IColumn<RowContact>) => {
  const customField = row.origin.custom_fields.find((field) => field.field_key === column.field)

  if (!customField || !customField.value.value) {
    return null
  }

  if (customField.value.type === 'date') {
    return uiStore.dayjs(customField.value.value).format(DayjsFormats.full6)
  }

  if (customField.value.type === 'datetime') {
    return uiStore.dayjs(customField.value.value).format(DayjsFormats.fullWithAtDash2)
  }

  return customField.value.value
}

const getNumberStatusValue = (row: RowContact, column: IColumn<RowContact>) => {
  return row.fields[column.field] ? 'Invalid' : 'Valid'
}

const getCellForKeywords = (row: RowContact) => {
  if (!row.origin.keywords.length) {
    return <EmptyCell />
  }

  return (
    <ItemsWithCount
      items={row.origin.keywords}
      itemComponent={KeywordItem}
      containerWidth={362}
      cacheScope={'keywords'}
    />
  )
}

const getCellForOptOut = (row: RowContact, column: IColumn<RowContact>) => {
  const optOut = row.origin.shortcode_opt_outs.find(
    ({ id }) => String(id) === column.field.split('.').pop()
  )
  const correctDate =
    optOut?.timestamp &&
    uiStore.dayjs(String(optOut?.timestamp)).format(DayjsFormats.fullWithAtDash2)

  return correctDate || <EmptyCell />
}

const getCellForSource = (row: RowContact, column: IColumn<RowContact>) => {
  const currentValue = column?.values?.find((value) => String(value.key) === row.origin.source)

  return currentValue?.label
}

const getCellForAreaCode = (row: RowContact) => {
  return row.origin.area_code
}

const getCellForOptInStatus = (row: RowContact) => {
  const optOut = row.origin.opt_out

  return optOut !== null ? 'Opted-out' : optOut
}

const isCellWithoutContent = (row: RowContact) => row.id === -1
const withAreaLabelColumns = ['name', 'number', 'email']

type IFiltersResponseList = {
  filters: FilterItemConfig[]
  customFilters: FilterItemConfig[]
}

export const columnTransformer = (
  filtersList: IFiltersResponseList,
  activeFields: string[],
  onOpenContactsDetails: IOpenContactsDetails
): IColumn<RowContact>[] => {
  const preparedFilters = filterTransformer(filtersList)

  return activeFields.map((field) => {
    const customFilter = field.includes('custom.')
    let clearedField = field

    if (customFilter) {
      // Delete 'custom.' prefix
      clearedField = field.split('.').pop() || ''
    }

    const currentFilter = filtersList.filters.find((filter) => filter.key === field)

    const getName = () => {
      if (!customFilter && clearedField === 'tag') return 'Tags'

      return nameExceptions[clearedField] || preparedFilters[clearedField]
    }
    const name = getName()
    const values = currentFilter && Array.isArray(currentFilter?.values) ? currentFilter.values : []

    const preparedColumn: IColumn<RowContact> = {
      field: clearedField,
      name: name,
      values: values,
      ariaLabel: withAreaLabelColumns.includes(clearedField)
        ? `ContactRowCell_${clearedField}`
        : undefined,
    }
    if (sortByMapping[clearedField]) {
      preparedColumn.isSortable = true
    }

    if (customFilter) {
      return {
        name,
        field,
        isSortable: true,
        renderRowCell: getCustomRowCellValue,
      }
    }

    if (clearedField === tableColumnsKeys.name) {
      preparedColumn.renderRowCell = (row, column, config) =>
        getCellForName({ row: row.origin, config, onOpenContactsDetails })
      preparedColumn.width = '100%'
      preparedColumn.minWidth = '362px'
      preparedColumn.maxWidth = '362px'
    }

    if (clearedField === tableColumnsKeys.email) {
      preparedColumn.minWidth = '362px'
      preparedColumn.maxWidth = '362px'
    }

    if (clearedField === tableColumnsKeys.is_invalid) {
      preparedColumn.renderRowCell = getNumberStatusValue
    }

    if (clearedField === tableColumnsKeys.tag) {
      preparedColumn.renderRowCell = (row) =>
        getCellForTags({ tags: row.origin.tags, isDisabled: row.origin.isDisabled })
    }

    if (clearedField === tableColumnsKeys.source) {
      preparedColumn.renderRowCell = getCellForSource
    }

    if (clearedField === tableColumnsKeys.keyword) {
      preparedColumn.renderRowCell = getCellForKeywords
    }

    if (field.includes('opt_out_at_shortcode')) {
      preparedColumn.renderRowCell = getCellForOptOut
    }

    if (clearedField === tableColumnsKeys.number) {
      preparedColumn.renderRowCell = getCellNumber
    }

    if (clearedField === tableColumnsKeys.areaCode) {
      preparedColumn.renderRowCell = getCellForAreaCode
    }

    if (clearedField === 'opt_in') {
      preparedColumn.renderRowCell = getCellForOptInStatus
    }

    preparedColumn.isCellWithoutContent = isCellWithoutContent

    return preparedColumn
  })
}

export const sortByMapping: Record<string, string> = {
  name: 'first_name',
  number: 'number',
  email: 'email',
  lookup_type: 'lookup_type',
  keyword: 'keywords_count',
  tag: 'tags_count',
  created_at: 'created_at',
  is_invalid: 'is_invalid',
  carrier_name: 'carrier_name',
  updated_at: 'updated_at',
  opt_out_at: 'opt_out_at',
  source: 'source',
  area_code: 'area_codes.area_code',
  state_code: 'area_codes.state_code',
  time_zone: 'area_codes.time_zone',
  country: 'area_codes.country',
}
